import { reactive, computed, watch } from 'vue'
import { BESOIN_COMMUN_SECTIONS } from '../questionnaires/besoin-commun-schema.js'

/**
 * Validation bloc par bloc du tunnel « Analyse du besoin » (retour Figma) :
 * chaque section se confirme à part, et une section confirmée passe en lecture
 * seule. La case « Tout est OK » du Point de vigilance est dérivée : elle se
 * coche automatiquement quand toutes les sections applicables sont confirmées.
 *
 * Ce fichier tient l'ÉTAT des sections. La RÈGLE de confirmation — refusée
 * tant qu'une réponse obligatoire manque — vit dans `useBesoinConfirmGuard`.
 *
 * @param {Object} deps
 * @param {import('vue').Ref<Object|null>} deps.quote - Demande chargée (null pendant le chargement).
 * @param {import('vue').Ref<boolean>} deps.isQuoteSigned - Devis signé → tout est en lecture seule.
 * @param {import('vue').Ref<boolean>} deps.isEditing - Mode édition du détail de la demande.
 * @param {Function} deps.getValue - Accès aux champs de la demande (édités ou d'origine).
 */
export function useBesoinSectionsConfirm({ quote, isQuoteSigned, isEditing, getValue }) {
  const sectionsConfirmed = reactive({
    personal: false,
    client: false,
    formation: false,
    ...Object.fromEntries(BESOIN_COMMUN_SECTIONS.map((s) => [s.key, false])),
    disponibilites: false,
    lieu: false,
    financement: false
  })

  // Section verrouillée : devis signé (lecture seule globale) ou section confirmée.
  const sectionLocked = (key) => isQuoteSigned.value || sectionsConfirmed[key]

  // Le tunnel est en édition permanente, section par section : une section
  // confirmée repasse en lecture seule tant qu'on ne la décoche pas.
  const sectionEditing = (key) => isEditing.value && !sectionLocked(key)

  // Sections à confirmer : seules celles réellement affichées comptent (le bloc
  // client n'existe que pour une entreprise, le lieu qu'en INTRA — la session
  // INTER se confirme avec « Formation », où elle se choisit —, le
  // questionnaire spécifique n'existe pas pour toutes les formations).
  const applicableSections = computed(() => {
    // Garde : le footer du popup (QuoteBesoinModal) évalue canValider — donc
    // cette chaîne — via la ref exposée PENDANT le chargement de la demande ;
    // getValue lit quote.value et crasherait sur null. Tant que rien n'est
    // chargé, aucune section n'existe.
    if (!quote.value) return []
    const clientType = getValue('client_type')
    const lieu = getValue('formation_lieu')
    const sections = [
      { key: 'personal', label: 'Informations personnelles' },
      { key: 'formation', label: 'Formation' },
      // Sections communes avec le questionnaire d'organisation : titres tirés
      // du schéma partagé, pour ne pas les redire ici.
      ...BESOIN_COMMUN_SECTIONS.map((s) => ({ key: s.key, label: s.title })),
      { key: 'disponibilites', label: 'Disponibilités du client' },
      { key: 'financement', label: 'Financement' }
    ]
    if (clientType === 'professionnel') sections.splice(1, 0, { key: 'client', label: 'Informations Professionnelles' })
    // Le lieu se glisse juste après « Formation », comme dans le plan du tunnel :
    // ce qui reste à confirmer se cite dans l'ordre où on le rencontre.
    const apresFormation = sections.findIndex((s) => s.key === 'formation') + 1
    if (lieu === 'INTRA' || lieu === 'LOCATION') sections.splice(apresFormation, 0, { key: 'lieu', label: 'Détails du lieu de formation' })
    return sections
  })

  const unconfirmedSections = computed(() =>
    applicableSections.value.filter((s) => !sectionsConfirmed[s.key]).map((s) => s.label)
  )

  // « Tout est OK » : jamais cochable à la main, uniquement dérivé des sections.
  const allSectionsConfirmed = computed(() => unconfirmedSections.value.length === 0)

  // Changer le type de client ou le lieu de formation remplace ENTIÈREMENT le
  // contenu de la section correspondante (autre composant : Particulier ↔
  // Professionnel, INTRA ↔ INTER) : une confirmation donnée sur l'ancien contenu
  // ne vaut plus, on décoche pour forcer une relecture. La garde sur l'ancienne
  // valeur ignore l'hydratation initiale (null/undefined → valeur au chargement).
  watch(
    () => (quote.value ? getValue('client_type') : null),
    (val, old) => {
      if (old && val !== old) sectionsConfirmed.client = false
    }
  )
  watch(
    () => (quote.value ? getValue('formation_lieu') : null),
    (val, old) => {
      if (old && val !== old) sectionsConfirmed.lieu = false
    }
  )

  /** Nom d'une section applicable, tel que le bilan le cite. */
  const sectionLabel = (key) => applicableSections.value.find((s) => s.key === key)?.label || key

  /**
   * Restaure l'état des cases depuis une validation sauvegardée. Seules les clés
   * connues sont restaurées (liste blanche). Rétro-compatibilité : l'ancien
   * verrou global « Infos confirmées » coché vaut toutes les sections confirmées.
   *
   * @param {Object|null} savedSections - besoinData.sectionsConfirmed persisté.
   * @param {boolean} legacyInfosConfirmed - Ancien booléen global infosConfirmed.
   */
  function restoreSections(savedSections, legacyInfosConfirmed = false) {
    const hasSaved = savedSections && typeof savedSections === 'object'
    if (!hasSaved && !legacyInfosConfirmed) return
    Object.keys(sectionsConfirmed).forEach((key) => {
      sectionsConfirmed[key] = hasSaved ? savedSections[key] === true : true
    })
  }

  return {
    sectionsConfirmed,
    sectionLocked,
    sectionEditing,
    unconfirmedSections,
    allSectionsConfirmed,
    sectionLabel,
    restoreSections
  }
}
